import {
  DoubleSide,
  Group,
  Mesh,
  MeshStandardMaterial,
  PlaneGeometry,
} from 'three';
import { createShopSignTexture } from './theme.js';
import { addStaticCollider } from './util.js';
import { Parts } from './parts.js';
import { STATIONS } from './route.js';

/**
 * The black-market petrol seller.
 *
 * He sets up on the east pavement a few dozen metres past each station's exit,
 * where the queue for the pump is still visible behind him. No canopy, no pump,
 * no queue: a trestle table stacked with small oil bottles of petrol, a crate
 * of the same on the ground, and a row of jerry cans along the kerb. A
 * hand-painted board on two poles over his head reads بنزين.
 *
 * Stop beside him and one press fills the tank for a flat fee — he is more
 * expensive than the pump, which is free, but he takes a second instead of the
 * several you spend holding the lever at a working station.
 */

/** Metres past the station exit. Clear of the forecourt, its driveways and the
 *  tea stands, and short of the next cross street. */
const SETBACK = 44;

/** Sidewalk centre, matching the tea stands so the two read as one street. */
const SIDEWALK_LATERAL = 12.3;
const SIDEWALK_Y = 0.055;

const COLORS = {
  wood: 0x8a6a45,
  woodDark: 0x6a4f31,
  cloth: 0x2f5d6b,
  ply: 0xb9a888,
  steel: 0x6d6a62,
  bottleAmber: 0xc98a2e,
  bottleRed: 0xb0472f,
  bottleYellow: 0xd9b23f,
  bottlePale: 0xbda96f,
  capRed: 0xa8342a,
  capBlack: 0x2b2722,
  canGreen: 0x2f6b52,
  canRed: 0xa63a2c,
  canYellow: 0xc9a02c,
  canDark: 0x24523f,
  skin: 0x6a4b36,
  galabeya: 0xded5c0,
  cap: 0xe6dfcc,
  shoe: 0x4a3a2c,
  rubber: 0x22201d,
  funnel: 0xc8c2b2,
};

const BOTTLE_BODIES = [
  COLORS.bottleAmber,
  COLORS.bottleYellow,
  COLORS.bottleRed,
  COLORS.bottlePale,
];
const BOTTLE_CAPS = [COLORS.capRed, COLORS.capBlack, COLORS.canDark];
const CAN_BODIES = [COLORS.canGreen, COLORS.canRed, COLORS.canYellow];

export function buildPetrolStands(level, route) {
  const bodyMaterial = new MeshStandardMaterial({
    vertexColors: true,
    roughness: 0.9,
    metalness: 0.04,
  });
  const signMaterial = new MeshStandardMaterial({
    map: createShopSignTexture('بنزين', {
      background: '#efe4cf',
      foreground: '#24312b',
      accent: '#b73c2d',
    }),
    roughness: 0.86,
    metalness: 0.02,
    side: DoubleSide,
  });

  const stands = [];

  for (const spec of STATIONS) {
    const s = spec.s + SETBACK;
    const frame = route.offsetFrame(s, SIDEWALK_LATERAL);
    const group = new Group();
    group.position.set(frame.position.x, SIDEWALK_Y, frame.position.z);
    group.rotation.y = frame.yaw;
    group.name = `petrol-stand-${spec.id}`;
    level.root.add(group);

    const parts = new Parts();
    buildStand(parts);
    const merged = parts.merge();
    if (merged) {
      const body = new Mesh(merged, bodyMaterial);
      body.castShadow = true;
      body.receiveShadow = true;
      body.name = 'petrol-stand-body';
      group.add(body);
    }

    // The board sits on its own plane so the hand-painted texture can face the
    // road without a six-material box. It hangs a centimetre clear of the
    // board's face — coplanar with it, the plane disappears inside the box.
    const sign = new Mesh(new PlaneGeometry(1.72, 0.52), signMaterial);
    sign.position.set(0.465, 2.0, 0);
    sign.rotation.y = -Math.PI / 2;
    sign.castShadow = false;
    sign.name = 'petrol-sign';
    group.add(sign);

    // The stall and the man behind it, then the cans along the kerb.
    addStaticCollider(level, {
      x: group.position.x,
      y: 0.7,
      z: group.position.z,
      width: 1.8,
      height: 1.4,
      depth: 2.3,
      yaw: frame.yaw,
    });
    const canCentre = frame.position.clone().addScaledVector(frame.right, -0.75);
    addStaticCollider(level, {
      x: canCentre.x,
      y: 0.24,
      z: canCentre.z,
      width: 0.8,
      height: 0.48,
      depth: 1.9,
      yaw: frame.yaw,
    });

    stands.push({
      id: spec.id,
      station: spec,
      s,
      group,
      position: frame.position.clone(),
      yaw: frame.yaw,
      purchases: 0,
    });
  }

  return {
    stands,
    count: stands.length,
    sites: stands.map((stand) => stand.s),
  };
}

/* ------------------------------------------------------------------ */
/* Geometry                                                            */
/* ------------------------------------------------------------------ */

/**
 * Built in local space, where -X points at the road and +Z runs back down the
 * street. The counter faces -X; the seller stands behind it at +X.
 */
function buildStand(parts) {
  // Trestle: a plank top on four legs, with a cloth down the front.
  parts.box(0.74, 0.05, 1.96, COLORS.wood, 0, 0.8, 0);
  parts.box(0.78, 0.62, 2.0, COLORS.cloth, 0, 0.49, 0);
  for (const x of [-0.3, 0.3]) {
    for (const z of [-0.86, 0.86]) {
      parts.box(0.05, 0.8, 0.05, COLORS.woodDark, x, 0.4, z);
    }
  }

  // The bottles on the counter: two rows, staggered so the shelf reads as full.
  for (let row = 0; row < 2; row += 1) {
    const x = row === 0 ? -0.15 : 0.15;
    for (let i = 0; i < 8; i += 1) {
      const z = -0.8 + i * 0.228;
      addBottle(parts, x, 0.825, z, i + row);
    }
  }

  // A crate on the ground with the rest of the stock, caps showing.
  parts.box(0.44, 0.24, 0.62, COLORS.woodDark, 0.52, 0.12, -0.86);
  parts.box(0.4, 0.06, 0.58, COLORS.wood, 0.52, 0.24, -0.86);
  for (let i = 0; i < 5; i += 1) {
    addBottle(parts, 0.42 + (i % 2) * 0.16, 0.27, -1.06 + Math.floor(i / 2) * 0.19, i + 2);
  }

  // Funnel left on the counter, and a rag beside it.
  parts.cyl(0.085, 0.02, 0.12, 10, COLORS.funnel, 0.16, 0.885, 0.62);
  parts.cyl(0.018, 0.018, 0.06, 6, COLORS.funnel, 0.16, 0.8, 0.62);
  parts.box(0.16, 0.012, 0.22, COLORS.cap, -0.2, 0.832, 0.66);

  // Two poles and the board they carry.
  for (const z of [-0.82, 0.82]) {
    parts.cyl(0.028, 0.03, 2.3, 6, COLORS.steel, 0.5, 1.15, z);
  }
  parts.box(0.05, 0.56, 1.8, COLORS.ply, 0.5, 2.0, 0);

  // The jerry cans along the kerb: four standing, one on its side.
  addJerryCan(parts, -0.72, -0.66, 0, CAN_BODIES[0]);
  addJerryCan(parts, -0.74, -0.22, 0.06, CAN_BODIES[1]);
  addJerryCan(parts, -0.7, 0.24, -0.05, CAN_BODIES[2]);
  addJerryCan(parts, -0.73, 0.72, 0.1, CAN_BODIES[0], true);
  addJerryCan(parts, 0.2, 1.12, 0.4, CAN_BODIES[1]);

  // An old tyre lying flat at the end of the counter, hub up. Standing it on
  // edge only ever showed the road a black disc.
  parts.cyl(0.31, 0.31, 0.16, 14, COLORS.rubber, -0.5, 0.08, 1.08);
  parts.cyl(0.17, 0.17, 0.03, 10, COLORS.steel, -0.5, 0.175, 1.08);

  // The seller, behind the counter, facing the road.
  parts.group(0.66, 0, 0.34, Math.PI / 2, () => addVendor(parts));
}

/** One small oil bottle: body, neck, cap. `y` is the surface it stands on. */
function addBottle(parts, x, y, z, seed) {
  const body = BOTTLE_BODIES[seed % BOTTLE_BODIES.length];
  const cap = BOTTLE_CAPS[seed % BOTTLE_CAPS.length];

  parts.cyl(0.041, 0.045, 0.18, 8, body, x, y + 0.09, z);
  parts.cyl(0.019, 0.026, 0.06, 6, body, x, y + 0.21, z);
  parts.cyl(0.023, 0.023, 0.03, 6, cap, x, y + 0.255, z);
}

/** A 20-litre jerry can. `lying` swaps its footprint so it reads as tipped over. */
function addJerryCan(parts, x, z, yaw, color, lying = false) {
  parts.group(x, 0, z, yaw, () => {
    const height = lying ? 0.34 : 0.46;
    const depth = lying ? 0.46 : 0.34;

    parts.box(0.19, height, depth, color, 0, height / 2, 0);
    // Ribs across the flat face.
    parts.box(0.2, 0.03, depth * 0.72, COLORS.canDark, 0, height * 0.42, 0);
    parts.box(0.2, 0.03, depth * 0.72, COLORS.canDark, 0, height * 0.62, 0);
    // Cap and handle on the top.
    parts.cyl(0.032, 0.032, 0.045, 8, COLORS.canDark, 0.045, height + 0.02, depth * 0.3);
    parts.box(0.11, 0.03, 0.05, COLORS.canDark, -0.03, height + 0.02, -depth * 0.28);
  });
}

/**
 * The seller: a man in a galabeya and a skullcap, one bottle in his hand,
 * built facing local -Z so the caller can rotate him towards the road.
 */
function addVendor(parts) {
  // Galabeya, hem to shoulders.
  parts.cyl(0.185, 0.275, 1.16, 9, COLORS.galabeya, 0, 0.58, 0);
  parts.cyl(0.2, 0.2, 0.1, 9, COLORS.galabeya, 0, 1.2, 0);

  // Neck, head, skullcap.
  parts.cyl(0.055, 0.06, 0.09, 6, COLORS.skin, 0, 1.27, 0);
  parts.sphere(0.125, 10, 8, COLORS.skin, 0, 1.39, 0);
  parts.cyl(0.118, 0.128, 0.075, 10, COLORS.cap, 0, 1.475, 0);

  // Arms, hanging.
  for (const x of [-0.19, 0.19]) {
    parts.cyl(0.045, 0.052, 0.42, 6, COLORS.galabeya, x, 0.96, 0);
  }

  // Feet.
  for (const x of [-0.11, 0.11]) {
    parts.box(0.13, 0.06, 0.24, COLORS.shoe, x, 0.03, 0.05);
  }

  // A bottle held up in his right hand, as if he has just pulled it off the
  // shelf and is offering it over the counter.
  parts.cyl(0.043, 0.046, 0.19, 8, COLORS.bottleAmber, -0.2, 0.86, -0.12);
  parts.cyl(0.02, 0.027, 0.06, 6, COLORS.bottleAmber, -0.2, 0.985, -0.12);
  parts.cyl(0.024, 0.024, 0.03, 6, COLORS.capRed, -0.2, 1.03, -0.12);
}
